import { formatDateTimeReadable, WorkflowPlace } from '@nemuru-eng/js-api-client';

import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { CreditCardWorkflow } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface WorkflowStepProps {
  step: WorkflowPlace;
  workflow: CreditCardWorkflow;
}

export const WorkflowStep = ({ step, workflow }: WorkflowStepProps) => {
  const { palette } = useCustomTheme();

  const currentPlaceLogs = workflow.logs
    .filter((log) => log.from === step)
    .sort((a, b) => new Date(a.createdAt ?? '').getTime() - new Date(b.createdAt ?? '').getTime());

  const isRenderedStepTheCurrentWorkflowStep = workflow.place.name === step;
  const isRenderedStepTheDeniedOne = step === WorkflowPlace.DENIED;
  const isRenderedStepTheCompletedOne = step === WorkflowPlace.COMPLETED;

  const currentPlaceLog = currentPlaceLogs[0];
  const isRenderedStepCompleted = currentPlaceLogs.length > 0;
  const isRenderedStepAPendingOne = !currentPlaceLogs.length && !isRenderedStepTheCurrentWorkflowStep;

  const hasWorkflowBeenCompleted = [WorkflowPlace.DENIED, WorkflowPlace.COMPLETED].includes(workflow.place.name);

  const getIconProps = () => {
    if (isRenderedStepTheDeniedOne) {
      return {
        name: 'close',
        color: palette.secondary.main,
      };
    }

    if (isRenderedStepTheCompletedOne) {
      return {
        name: 'check',
        color: palette.success.main,
      };
    }

    if (isRenderedStepTheCurrentWorkflowStep) {
      return {
        name: 'arrow_forward',
        color: palette.secondary.main,
      };
    }

    if (isRenderedStepCompleted) {
      return {
        name: 'check',
        color: palette.success.main,
      };
    }

    return {
      name: 'hourglass_empty',
      color: palette.grey[400],
    };
  };

  const iconProps = getIconProps();

  if (!hasWorkflowBeenCompleted && (isRenderedStepTheCompletedOne || isRenderedStepTheDeniedOne)) {
    return null;
  }

  return (
    <FlexRow gridGap={12} width="100%" style={{ opacity: isRenderedStepAPendingOne ? 0.5 : 1 }}>
      <FlexColumn alignItems={'center'} gridGap={4} flexShrink={0}>
        <Icon size={16} color={iconProps.color}>
          {iconProps.name}
        </Icon>
      </FlexColumn>
      <FlexRow width="100%" minWidth={0} gridGap={16}>
        <Font variant={'body2'} color="textSecondary" className="overflow-text-ellipsis">
          <Text strong tId={step} />
        </Font>
        {currentPlaceLog?.createdAt && (
          <Font
            variant={'caption'}
            color={'textSecondary'}
            display="block"
            align="right"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {formatDateTimeReadable(currentPlaceLog.createdAt)}
          </Font>
        )}
      </FlexRow>
    </FlexRow>
  );
};
